import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { booksAPI, salesAPI } from "@/services/api";
import { useDirectionLanguage } from "@/components/DirectionLanguageContext";

interface Book {
  id: string;
  name: string;
  author: string;
  category: string;
  stock: number;
  price: number;
  min_stocks?: number;
  version?: number;
  parent_book_id?: string;
}

interface CartItem {
  book_id: string;
  book_name: string;
  quantity: number;
  price_per_book: number;
  subtotal: number;
  discount: number; // This now represents a percentage
}

interface SaleData {
  id?: string;
  sale_date: string;
  buyer_name: string;
  buyer_phone: string;
  client_id: string;
  total_price: number;
  items: CartItem[];
}

const AddSalePage: React.FC = () => {
  const { t } = useTranslation();
  const [books, setBooks] = useState<Book[]>([]);
  const [selectedBook, setSelectedBook] = useState<string | null>(null);
  const [selectedBookDisplay, setSelectedBookDisplay] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [discount, setDiscount] = useState<number>(0);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [buyerName, setBuyerName] = useState<string>("");
  const [buyerPhone, setBuyerPhone] = useState<string>("");
  const [showBuyerModal, setShowBuyerModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isRtl } = useDirectionLanguage();

  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      setError(null);

      const storedClientId = localStorage.getItem("clientId");

      if (!storedClientId) {
        setError(t("client_id_required"));
        setLoading(false);
        return;
      }

      try {
        const booksData = await booksAPI.getAll(storedClientId, {
          includeVersions: true,
        });

        // Process books to ensure consistent field naming
        const processedBooks = booksData.map((book: any) => ({
          id: book.id,
          name: book.name,
          author: book.author || "",
          category: book.category || "",
          stock: Number(book.stock) || 0,
          price: Number(book.price_per_book) || 0,
          min_stocks: Number(book.min_stocks) || 0,
          version: Number(book.version) || 1,
          parent_book_id: book.parent_book_id || null,
        }));

        setBooks(processedBooks);
        setLoading(false);
      } catch (error: any) {
        setError(error.message || t("fetch_books_error"));
        setLoading(false);
      }
    };

    fetchBooks();
  }, [t]);

  const handleBookChange = (bookId: string) => {
    setSelectedBook(bookId);
    const book = books.find((b) => String(b.id) === String(bookId));

    setSelectedBookDisplay(book ? book.name : "");
    setDiscount(0); // Reset discount when book changes
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuantity(
      Math.min(parseInt(e.target.value, 10) || 1, getAvailableStock()),
    );
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const discountValue = parseFloat(e.target.value) || 0;
    // Limit discount percentage between 0 and 100
    setDiscount(Math.min(Math.max(0, discountValue), 100));
  };

  const getAvailableStock = () => {
    const selectedBookData = books.find(
      (b) => String(b.id) === String(selectedBook),
    );

    console.log("Selected book data:", selectedBookData);

    return selectedBookData ? selectedBookData.stock : 0;
  };

  const calculateSubtotal = (price: number, quantity: number, discountPercentage: number) => {
    const rawSubtotal = price * quantity;
    const discountAmount = (rawSubtotal * discountPercentage) / 100;
    return Math.max(0, rawSubtotal - discountAmount);
  };

  const getDiscountAmount = (price: number, quantity: number, discountPercentage: number) => {
    const rawSubtotal = price * quantity;
    return (rawSubtotal * discountPercentage) / 100;
  };

  // Function to filter books based on version logic and stock availability
  const getAvailableBooksForSale = () => {
    // First filter out books with no stock
    const booksWithStock = books.filter((book) => book.stock > 0);
    
    console.log("Books with stock:", booksWithStock.map(b => ({ id: b.id, name: b.name, version: b.version, parent_book_id: b.parent_book_id, stock: b.stock })));
    
    // Group books by their original book (either parent_book_id or their own id)
    const bookGroups: { [key: string]: Book[] } = {};
    
    booksWithStock.forEach((book) => {
      // Get the original book ID (parent_book_id if exists, otherwise the book's own id)
      const originalBookId = book.parent_book_id || book.id;
      
      if (!bookGroups[originalBookId]) {
        bookGroups[originalBookId] = [];
      }
      bookGroups[originalBookId].push(book);
    });
    
    console.log("Book groups:", Object.keys(bookGroups).map(key => ({
      originalId: key,
      books: bookGroups[key].map(b => ({ id: b.id, name: b.name, version: b.version, stock: b.stock }))
    })));
    
    const availableBooks: Book[] = [];
    
    // For each book group, determine which version to show
    Object.values(bookGroups).forEach((group) => {
      // Sort by version (ascending)
      group.sort((a, b) => (a.version || 1) - (b.version || 1));
      
      // Check if there are multiple versions in this group
      if (group.length === 1) {
        // Only one version, add it directly
        availableBooks.push(group[0]);
        console.log(`Single version book added: ${group[0].name} (v${group[0].version})`);
      } else {
        // Multiple versions exist, apply version logic
        let bookToShow = null;
        
        // Check versions from oldest to newest
        for (let i = 0; i < group.length; i++) {
          const currentBook = group[i];
          
          if (currentBook.stock > 0) {
            // This version has stock
            bookToShow = currentBook;
            console.log(`Selected version: ${currentBook.name} (v${currentBook.version}) - oldest with stock`);
            break; // Show the oldest version that has stock
          }
        }
        
        // If we found a book to show, add it
        if (bookToShow) {
          availableBooks.push(bookToShow);
        }
      }
    });
    
    console.log("Final available books for sale:", availableBooks.map(b => ({ id: b.id, name: b.name, version: b.version, stock: b.stock })));
    
    return availableBooks;
  };

  const updateCartItemQuantity = (bookId: string, newQuantity: number) => {
    if (newQuantity <= 0) return;
    
    const book = books.find((b) => String(b.id) === String(bookId));
    if (!book) return;

    // Validate against available stock
    if (newQuantity > book.stock) {
      alert(t("not_enough_stock"));
      return;
    }

    // Create a deep copy of the cart to avoid reference issues
    const updatedCart = cart.map((item) => {
      if (String(item.book_id) === String(bookId)) {
        const subtotal = calculateSubtotal(item.price_per_book, newQuantity, item.discount);
        return {
          ...item,
          quantity: newQuantity,
          subtotal,
        };
      }
      return {...item}; // Return a copy of each item, not the reference
    });

    setCart(updatedCart);
    const newTotal = updatedCart.reduce((acc, item) => acc + item.subtotal, 0);
    setTotalPrice(newTotal);
  };

  const updateCartItemDiscount = (bookId: string, newDiscountPercentage: number) => {
    if (newDiscountPercentage < 0 || newDiscountPercentage > 100) return;
    
    // Create a deep copy of the cart to avoid reference issues
    const updatedCart = cart.map((item) => {
      if (String(item.book_id) === String(bookId)) {
        const subtotal = calculateSubtotal(item.price_per_book, item.quantity, newDiscountPercentage);
        return {
          ...item,
          discount: newDiscountPercentage,
          subtotal,
        };
      }
      return {...item}; // Return a copy of each item, not the reference
    });

    setCart(updatedCart);
    const newTotal = updatedCart.reduce((acc, item) => acc + item.subtotal, 0);
    setTotalPrice(newTotal);
  };

  const addToCart = async () => {
    if (selectedBook) {
      const book = books.find((b) => String(b.id) === String(selectedBook));

      console.log("Adding to cart:", book);

      if (!book) {
        console.error("Book not found in list!");
        return;
      }

      if (quantity > book.stock) {
        alert(t("not_enough_stock"));
        return;
      }

      const subtotal = calculateSubtotal(book.price, quantity, discount);
      
      const existingItemIndex = cart.findIndex(
        (item) => String(item.book_id) === String(book.id),
      );

      if (existingItemIndex > -1) {
        // Update existing item
        const updatedCart = [...cart];
        const currentItem = updatedCart[existingItemIndex];
        const newQuantity = currentItem.quantity + quantity;
        // Keep the existing discount percentage
        const newSubtotal = calculateSubtotal(book.price, newQuantity, currentItem.discount);
        
        updatedCart[existingItemIndex] = {
          ...currentItem,
          quantity: newQuantity,
          subtotal: newSubtotal,
        };
        setCart(updatedCart);

        // Recalculate total price
        const newTotalPrice = updatedCart.reduce(
          (sum, item) => sum + item.subtotal,
          0,
        );

        setTotalPrice(newTotalPrice);
      } else {
        // Add new item
        const newCartItem: CartItem = {
          book_id: book.id,
          book_name: book.name,
          quantity,
          price_per_book: book.price,
          discount,
          subtotal,
        };

        const newCart = [...cart, newCartItem];
        setCart(newCart);

        // Recalculate total price
        const newTotalPrice = newCart.reduce(
          (sum, item) => sum + item.subtotal,
          0,
        );

        setTotalPrice(newTotalPrice);
      }

      setSelectedBook(null);
      setSelectedBookDisplay("");
      setQuantity(1);
      setDiscount(0);
    }
  };

  const handleRemove = (bookId: string) => {
    const updatedCart = cart.filter((item) => item.book_id !== bookId);
    setCart(updatedCart);
    const newTotal = updatedCart.reduce((acc, item) => acc + item.subtotal, 0);
    setTotalPrice(newTotal);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const storedClientId = localStorage.getItem("clientId");

    if (!storedClientId) {
      alert(t("client_id_required"));
      setLoading(false);
      return;
    }

    if (cart.length === 0) {
      alert(t("cart_empty"));
      setLoading(false);
      return;
    }

    if (!buyerName) {
      alert(t("buyer_name_required"));
      setLoading(false);
      return;
    }

    try {
      console.log("Starting sale submission with client ID:", storedClientId);

      // Create a single sale with multiple items
      const saleData: SaleData = {
        sale_date: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
        buyer_name: buyerName,
        buyer_phone: buyerPhone || "",
        client_id: storedClientId,
        total_price: totalPrice,
        items: cart,
      };

      console.log("Submitting sale with data:", saleData);

      // Create the sale record - this API needs to be updated to support multiple items
      const response = await salesAPI.create(saleData);
      
      // Debug - log the response
      console.log("Sale creation response:", response);
      
      // If there's an invoice URL in the response, open it in a new tab
      if (response && response.invoice_url) {
        // Debug - log the invoice URL
        console.log("Invoice URL from response:", response.invoice_url);
        
        // Fix potential URL issue - ensure we're using the web route not the API route
        let invoiceUrl = response.invoice_url;
        if (invoiceUrl.includes('/api/sales/')) {
          invoiceUrl = invoiceUrl.replace('/api/sales/', '/sales/');
          console.log("Fixed invoice URL:", invoiceUrl);
        }
        
        // Open invoice in new tab and store the window reference
        const newWindow = window.open(invoiceUrl, '_blank');
        
        // Check if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          alert(t("sales_recorded_successfully") + ". " + t("invoice_popup_blocked"));
          console.log("Invoice URL:", response.invoice_url);
        } else {
          alert(t("sales_recorded_successfully"));
        }
      } else {
      alert(t("sales_recorded_successfully"));
      }
      
      setLoading(false);
      navigate("/sales");
    } catch (error: any) {
      console.error("Error recording sales:", error);
      const errorMessage =
        error.response?.data?.details ||
        error.message ||
        t("sales_record_error");

      alert(`${t("sales_record_error")}: ${errorMessage}`);
      setLoading(false);
    }
  };

  const columns = [
    { uid: "srno", name: t("S.No") },
    { uid: "book_name", name: t("book_name") },
    { uid: "quantity", name: t("quantity") },
    { uid: "price_per_book", name: t("price_per_book") },
    { uid: "discount", name: t("discount") + " (%)" },
    { uid: "discount_amount", name: t("discount_amount") },
    { uid: "subtotal", name: t("subtotal") },
    { uid: "actions", name: t("actions") },
  ];

  type ColumnKey = keyof CartItem | "actions" | "discount_amount" | "srno";

  const renderCell = (item: CartItem, columnKey: ColumnKey) => {
    switch (columnKey) {
      case "srno":
        // Find the index of the current item in the cart array
        return cart.findIndex(cartItem => cartItem.book_id === item.book_id) + 1;
      case "book_name":
        return item.book_name;
      case "quantity":
        return (
          <div className="flex items-center gap-2">
            <Button
              isIconOnly
              className="min-w-8 h-8"
              color="default"
              size="sm"
              onClick={() => updateCartItemQuantity(item.book_id, item.quantity - 1)}
            >
              -
            </Button>
            <Input
              className="w-14 min-w-0"
              size="sm"
              type="number"
              value={item.quantity.toString()}
              onChange={(e) => {
                const newQty = parseInt(e.target.value, 10) || 1;
                updateCartItemQuantity(item.book_id, newQty);
              }}
            />
            <Button
              isIconOnly
              className="min-w-8 h-8"
              color="default"
              size="sm"
              onClick={() => {
                const book = books.find((b) => String(b.id) === String(item.book_id));
                const maxStock = book ? book.stock : item.quantity;
                updateCartItemQuantity(item.book_id, Math.min(item.quantity + 1, maxStock));
              }}
            >
              +
            </Button>
          </div>
        );
      case "price_per_book":
        return item.price_per_book;
      case "discount":
        return (
          <Input
            className="w-20 min-w-0"
            size="sm"
            type="number"
            value={item.discount.toString()}
            min={0}
            max={100}
            onChange={(e) => {
              const newDiscount = parseFloat(e.target.value) || 0;
              updateCartItemDiscount(item.book_id, Math.min(Math.max(0, newDiscount), 100));
            }}
            endContent={<div className="pointer-events-none">%</div>}
          />
        );
      case "discount_amount":
        return getDiscountAmount(item.price_per_book, item.quantity, item.discount).toFixed(2);
      case "subtotal":
        return item.subtotal.toFixed(2);
      case "actions":
        return (
          <Button
            color="danger"
            disabled={loading}
            size="sm"
            onClick={() => handleRemove(item.book_id)}
          >
            {t("remove")}
          </Button>
        );
      default:
        return null;
    }
  };

  // Inline add-to-cart form handler
  const handleAddToCart = (e: React.FormEvent) => {
    e.preventDefault();
    addToCart();
  };

  return (
    <DefaultLayout>
      <h1 className="text-3xl font-bold text-center mb-8">{t("add_sale")}</h1>
      <section
        className="row container px-8 flex flex-col items-center justify-center gap-4 py-2"
        dir={isRtl ? "rtl" : "ltr"}
      >
        {error && (
          <div className="w-full p-4 mb-4 text-white bg-red-500 rounded">
            {error}
          </div>
        )}

        {/* Book Selection and Add to Cart Form */}
        <div className="flex flex-col w-full max-w-3xl gap-4 bg-gray-50 p-4 rounded-lg">
          <form
            className="flex flex-col md:flex-row items-end gap-4 w-full"
            onSubmit={handleAddToCart}
          >
            <div className="flex-1">
              <label
                className="block text-gray-700 mb-1 font-semibold"
                htmlFor="book"
              >
                {t("book")}
              </label>
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    className="w-full justify-between"
                    endContent={<span>▼</span>}
                    variant="flat"
                  >
                    {selectedBookDisplay || t("select_book")}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label={t("book_selection")}
                  className="max-h-80 overflow-y-auto"
                >
                  {getAvailableBooksForSale()
                    .map((book) => (
                      <DropdownItem
                        key={book.id}
                        className="text-black"
                        onClick={() => handleBookChange(book.id)}
                      >
                        {book.name} - ({book.stock} {t("in_stock")})
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </Dropdown>
            </div>

            <div className="w-36">
              <label
                className="block text-gray-700 mb-1 font-semibold"
                htmlFor="quantity"
              >
                {t("quantity")}
              </label>
              <div className="flex items-center gap-1">
                <Button
                  isIconOnly
                  className="min-w-8 h-8"
                  color="default"
                  disabled={!selectedBook}
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  -
                </Button>
                <Input
                  className="flex-1"
                  disabled={!selectedBook}
                  id="quantity"
                  max={getAvailableStock()}
                  min="1"
                  type="number"
                  value={quantity.toString()}
                  onChange={handleQuantityChange}
                />
                <Button
                  isIconOnly
                  className="min-w-8 h-8"
                  color="default"
                  disabled={!selectedBook}
                  size="sm"
                  onClick={() =>
                    setQuantity(Math.min(quantity + 1, getAvailableStock()))
                  }
                >
                  +
                </Button>
              </div>
            </div>

            <div className="w-36">
              <label
                className="block text-gray-700 mb-1 font-semibold"
                htmlFor="discount"
              >
                {t("discount")} (%)
              </label>
              <Input
                className="flex-1"
                disabled={!selectedBook}
                id="discount"
                min="0"
                max="100"
                type="number"
                value={discount.toString()}
                onChange={handleDiscountChange}
                endContent={<div className="pointer-events-none">%</div>}
              />
            </div>

            <Button
              className="h-12 px-8"
              color="primary"
              disabled={!selectedBook || loading}
              size="lg"
              type="submit"
            >
              {t("add_to_cart")}
            </Button>
          </form>
        </div>

        {/* Cart Section */}
        <div className="w-full max-w-3xl bg-white p-4 rounded-lg shadow-sm mt-4">
          <h2 className="text-xl font-bold mb-4">{t("shopping_cart")}</h2>

          {loading && (
            <div className="flex justify-center py-8">
              <Spinner color="primary" size="lg" />
            </div>
          )}

          {!loading && (
            <>
              <Table aria-label={t("sales_cart")} className="mb-4">
                <TableHeader columns={columns}>
                  {(column) => (
                    <TableColumn
                      key={column.uid}
                      className="text-center font-bold"
                    >
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody emptyContent={t("cart_empty_message")} items={cart}>
                  {(item) => (
                    <TableRow key={item.book_id} className="text-center">
                      {columns.map((column) => (
                        <TableCell key={column.uid}>
                          {renderCell(item, column.uid as ColumnKey)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
                <div className="text-xl font-bold">
                  {t("total_price")}:{" "}
                  <span className="text-2xl">{totalPrice.toFixed(2)}</span>
                </div>
                <Button
                  className="px-8"
                  color="success"
                  disabled={cart.length === 0 || loading}
                  size="lg"
                  onClick={() => setShowBuyerModal(true)}
                >
                  {t("complete_sale")}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Buyer Modal */}
        <Modal isOpen={showBuyerModal} onClose={() => setShowBuyerModal(false)}>
          <ModalContent>
            <ModalHeader>
              <h2 className="text-xl font-bold">{t("buyer_details")}</h2>
            </ModalHeader>
            <ModalBody>
              <div className="mb-4">
                <label
                  className="block text-gray-700 font-medium mb-1"
                  htmlFor="buyerName"
                >
                  {t("buyer_name")}
                </label>
                <Input
                  required
                  className="form-input"
                  id="buyerName"
                  placeholder={t("enter_buyer_name")}
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 font-medium mb-1"
                  htmlFor="buyerPhone"
                >
                  {t("buyer_phone")}
                </label>
                <Input
                  className="form-input"
                  id="buyerPhone"
                  placeholder={t("enter_buyer_phone")}
                  value={buyerPhone}
                  onChange={(e) => setBuyerPhone(e.target.value)}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                className="w-full"
                color="success"
                disabled={loading || !buyerName}
                size="lg"
                onClick={handleSubmit}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Spinner color="white" size="sm" />
                    {t("processing")}
                  </div>
                ) : (
                  t("complete_purchase")
                )}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </section>
    </DefaultLayout>
  );
};

export default AddSalePage;
